import  { useState } from "react";
import "../../../../../css/CashflowModeller2/DialogBoxes/DialogBoxes.css";
import Close from "../Buttons/Close";
import Input2 from "../Inputs/Input2";
import InputMedium from "../Inputs/InputMedium";
import Input2Medium from "../Inputs/Input2Medium";
import InputDate from "../Inputs/InputDate";
import AddButton from "../Buttons/AddButton";
import RemoveButton from "../Buttons/RemoveButton";
import { useCfmContext } from "../../Context/CfmContext";
import TimelineOptions from "../../Utils/TimelineOptions";
import ATROptions from "../../Utils/ATROptions";
import InvestmentTypeOptions from "../../Utils/InvestmentTypeOptions";
import useNameOptions from "../../Utils/NameOptions";
import useDraggableDialog from "./Utils/UseDraggableDialog";
import getTotalIsaContribution from "../../Utils/GetTotalIsaContribution";
import Input1Big from "../Inputs/Input1Big";
import InputSmall from "../Inputs/InputSmall";

const defaultRow = {
  name: "",
  startDate: "",
  fundValue: { value: "", unit: TimelineOptions[1].value },
  contribution: { value: "", unit: TimelineOptions[1].value },
  atr: "",
  type: "",
  contStartDate: "",
  contEndDate: "",
};

/**
 * InvestmentDialogBox component allows users to add, edit, and remove investment entries.
 * Handles validation for duplicate investments and ISA contribution limits.
 *
 * @component
 * @param {Object} props - Component props.
 * @param {Function} props.onClose - Callback function to close the dialog box.
 *
 * @returns {JSX.Element} The rendered InvestmentDialogBox component.
 *
 * @example
 * <InvestmentDialogBox onClose={() => setShowDialog(false)} />
 *
 * @description
 * - Displays a draggable dialog for managing investments.
 * - Validates for duplicate investments per person and type.
 * - Enforces ISA contribution limits (annual/monthly).
 * - Allows inline editing and removal of investment rows.
 * - Uses context for state management and dispatching updates.
 */
const InvestmentDialogBox = ({ onClose }) => {
  const { state, dispatch } = useCfmContext();
  const [inputRow, setInputRow] = useState({ ...defaultRow });
  const [rows, setRows] = useState(state.investments || []);
  const { ref, position, handleMouseDown } = useDraggableDialog();
  // useClickOutside(dialogRef, onClose);
  const nameOptions = useNameOptions();
  const [error, setError] = useState("");
  const [errors, setErrors] = useState({});

  /**
   * Handles the addition of a new investment row.
   *
   * - Checks for duplicate investments by the same person and type.
   * - Validates ISA contribution limits (monthly and annual).
   * - If validation passes, adds the cleaned investment row to the state.
   * - Resets the input row and error state.
   * - Dispatches an action to update investments in the global state.
   *
   * @function
   * @returns {void}
   */
  const handleAdd = () => {
    const isDuplicate = rows.some(
      (row) =>
        row.name.trim() === inputRow.name.trim() &&
        row.type.trim() === inputRow.type.trim()
    );

    if (isDuplicate) {
      setError("Each person can only have one investment per type.");
      return;
    }

    // ISA contribution limit check
    const contributionValue = parseFloat(inputRow?.contribution?.value || 0);
    const contributionUnit = inputRow?.contribution?.unit;

    // if (
    //   inputRow.type === "ISA" &&
    //   ((contributionUnit === TimelineOptions[1].value &&
    //     contributionValue > 20000) ||
    //     (contributionUnit === TimelineOptions[0].value &&
    //       contributionValue > 1666.67))
    // ) {
    //   setError(
    //     `ISA contributions cannot exceed £20,000 per annum or £1,666.67 per month.`
    //   );
    //   return;
    // }

    if (inputRow.type === "ISA") {
      const totalISA = getTotalIsaContribution(
        state.investments,
        state.adHocData,
        inputRow.contribution.unit,
        inputRow.name
      );
      const newValue = parseFloat(inputRow.contribution.value || 0);

      const limit = inputRow.contribution.unit === "Monthly" ? 1666.67 : 20000;
      if (totalISA + newValue > limit) {
        setError(
          `Total ISA contributions across all sources cannot exceed £${limit.toLocaleString()} ${inputRow.contribution.unit.toLowerCase()}.`
        );
        return;
      }
    }

    /**
     * Cleans and normalizes an investment input row object.
     */
    const cleanedRow = {
      name: inputRow.name.trim() ?? "",
      startDate: inputRow.startDate ?? "",
      contStartDate: inputRow.contStartDate ?? "",
      contEndDate: inputRow.contEndDate ?? "",
      atr: inputRow.atr ?? "",
      type: inputRow.type ?? "",
      fundValue: {
        value: inputRow?.fundValue?.value ?? "",
        unit: inputRow?.fundValue?.unit ?? TimelineOptions[1].value,
      },
      contribution: {
        value: inputRow?.contribution?.value ?? "",
        unit: inputRow?.contribution?.unit ?? TimelineOptions[1].value,
      },
    };

    const updated = [...rows, cleanedRow];

    setRows(updated);
    setInputRow({ ...defaultRow });
    setError("");

    dispatch({
      type: "UPDATE_INVESTMENTS",
      payload: updated,
    });
  };

  /**
   * Handles changes to a specific field in a row of investment data.
   *
   * Updates the specified field of the row at the given index with the new value,
   * updates the local state with the modified rows, and dispatches an action to update
   * the investments in the global state.
   *
   * @param {number} index - The index of the row to update.
   * @param {string} field - The field name within the row to update.
   * @param {*} value - The new value to set for the specified field.
   */
  const handleRowChange = (index, field, value) => {
    const updated = [...rows];
    updated[index][field] = value;
    setRows(updated);
    dispatch({
      type: "UPDATE_INVESTMENTS",
      payload: updated,
    });
  };

  /**
   * Removes an investment row at the specified index from the rows array,
   * updates the state with the new array, and dispatches an action to update investments.
   *
   * @function
   * @param {number} index - The index of the row to remove.
   * @returns {void}
   */
  const handleRemove = (index) => {
    const updated = rows.filter((_, i) => i !== index);
    setRows(updated);
    dispatch({
      type: "UPDATE_INVESTMENTS",
      payload: updated,
    });
  };

  /**
   * Handles changes to input fields and updates the inputRow state accordingly.
   *
   * For fields "fundValue" and "contribution", updates the nested "value" property
   * while preserving other properties of the field object.
   * For all other fields, updates the field value directly.
   *
   * @param {string} field - The name of the field being updated.
   * @param {*} value - The new value to set for the specified field.
   */
  const handleInputChange = (field, value) => {
    if (field === "fundValue" || field === "contribution") {
      setInputRow((prev) => ({
        ...prev,
        [field]: {
          ...prev[field],
          value: value,
        },
      }));
    } else {
      setInputRow((prev) => ({
        ...prev,
        [field]: value,
      }));
    }
  };

  /**
   * Updates the unit value for a specific field in the input row state.
   *
   * @param {string} field - The name of the field to update.
   * @param {string} unit - The new unit value to set for the specified field.
   */
  const handleUnitChange = (field, unit) => {
    setInputRow((prev) => ({
      ...prev,
      [field]: {
        ...prev[field],
        unit: unit,
      },
    }));
  };

  const isAddDisabled =
    !inputRow.name ||
    !inputRow.startDate ||
    !inputRow.fundValue.value ||
    !inputRow.contribution.value ||
    !inputRow.atr ||
    !inputRow.type;

  return (
    <div
      ref={ref}
      className="dialog-box"
      style={{
        position: "absolute",
        left: `${position.x}px`,
        top: `${position.y}px`,
      }}
    >
      <div
        className="dialog-header"
        onMouseDown={handleMouseDown}
        style={{ cursor: "grab" }}
      >
        <div className="Title">Investment</div>
        <div className="dialog-header-buttons">
          <div className="Content-Small" onClick={onClose}>
            Close
          </div>
          <div onClick={onClose}>
            <Close />
          </div>
        </div>
      </div>

      <div className="dialog-row dialog-row-inputs single-row">
        <Input2Medium
          label="Name"
          value={inputRow.name ? inputRow.name : ""}
          onChange={(val) => handleInputChange("name", val)}
          options={nameOptions}
          type="invType"
        />
        <InputDate
          label="Inv. Start Date"
          value={inputRow.startDate ? inputRow.startDate : ""}
          onChange={(val) => handleInputChange("startDate", val)}
        />

        <Input1Big
          type="number"
          currency={true} // Assuming fund value is in currency
          label="Fund Value"
          value={inputRow.fundValue.value ? inputRow.fundValue.value : ""}
          unit={inputRow.fundValue.unit}
          onChange={(val) => handleInputChange("fundValue", val)}
          onUnitChange={(unit) => handleUnitChange("fundValue", unit)}
        />
        <Input2Medium
          label="Contribution"
          currency={true} // Assuming contribution is in currency
          value={inputRow.contribution.value ? inputRow.contribution.value : ""}
          // unit={inputRow.contribution.unit}
          onChange={(val) => handleInputChange("contribution", val)}
          onUnitChange={(unit) => handleUnitChange("contribution", unit)}
          options={TimelineOptions}
          currentUnit={inputRow.contribution.unit}
        />
        <InputSmall
          label="Start"
          value={inputRow.contStartDate ? inputRow.contStartDate : ""}
          onChange={(val) => handleInputChange("contStartDate", val)}
        />
        <InputSmall
          label="End"
          value={inputRow.contEndDate ? inputRow.contEndDate : ""}
          onChange={(val) => handleInputChange("contEndDate", val)}
        />
        <Input2Medium
          label="Growth Rate"
          type="per"
          per={true}
          value={inputRow.atr ? `${inputRow.atr}%` : ""}
          onChange={(val) => handleInputChange("atr", val)}
          options={ATROptions}
        />
        <Input2Medium
          label="Investment Type"
          type="invType"
          value={inputRow.type ? inputRow.type : ""}
          onChange={(val) => handleInputChange("type", val)}
          options={InvestmentTypeOptions}
          placeholderText="ISA"
        />
        <AddButton onClick={handleAdd} isDisabled={isAddDisabled} />
      </div>

      {error && <div className="error Label">{error}</div>}

      {rows?.map((row, index) => (
        <div key={index}>
          <div className="no-dovider-dialogBox" />
          <div className="dialog-row dialog-row-inputs">
            <Input2Medium
              value={row?.name ? row?.name : ""}
              onChange={(val) => handleRowChange(index, "name", val)}
              options={nameOptions}
              type="invType"
              readOnly
            />
            <InputDate
              value={row?.startDate ?? ""}
              onChange={(val) => handleRowChange(index, "startDate", val)}
              // readOnly
            />
            <Input1Big
              value={row?.fundValue?.value ?? ""}
              unit={row?.fundValue?.unit ?? ""}
              onChange={(val) => {
                if (val !== "") {
                  handleRowChange(index, "fundValue", {
                    ...row.fundValue,
                    value: val,
                  });
                }
              }}
              onUnitChange={(unit) =>
                handleRowChange(index, "fundValue", { ...row.fundValue, unit })
              }
              type="number"
              currency={true} // Assuming fund value is in currency
              // readOnly
            />
            <Input2Medium
              value={row?.contribution?.value ?? ""}
              unit={row?.contribution?.unit ?? ""}
              onChange={(val) => {
                const updated = [...rows];
                const current = {
                  ...updated[index],
                  contribution: { ...updated[index].contribution, value: val },
                };
                updated[index] = current;

                const isISA = current.type === "ISA";
                const unit = current.contribution.unit || "Annual";
                const limit = unit === "Monthly" ? 1666.67 : 20000;

                if (isISA) {
                  const filtered = updated.filter((_, i) => i !== index);

                  const total = getTotalIsaContribution(
                    filtered,
                    state.adHocData,
                    unit,
                    row.name
                  );
                  const maxAllowed = limit - total;
                  const parsedVal = parseFloat(val || 0);

                  if (parsedVal > maxAllowed) {
                    updated[index].contribution.value = maxAllowed;
                    setErrors((prev) => ({
                      ...prev,
                      [index]: `Total ISA limit is £${limit.toLocaleString()}. You can only contribute up to £${maxAllowed.toLocaleString()} for this period.`,
                    }));
                  } else {
                    setErrors((prev) => {
                      const copy = { ...prev };
                      delete copy[index];
                      return copy;
                    });
                  }
                }

                setRows(updated);
                dispatch({ type: "UPDATE_INVESTMENTS", payload: updated });
              }}
              onUnitChange={(unit) => {
                const updated = [...rows];
                updated[index].contribution.unit = unit;
                setRows(updated);
                dispatch({ type: "UPDATE_INVESTMENTS", payload: updated });
              }}
              currentUnit={row.contribution?.unit}
              options={TimelineOptions}
              isError={!!errors[index] && row.type === "ISA"}
            />
            <InputSmall
              value={row?.contStartDate ? row?.contStartDate : ""}
              onChange={(val) => handleRowChange(index, "contStartDate", val)}
            />
            <InputSmall
              value={row?.contEndDate ? row?.contEndDate : ""}
              onChange={(val) => handleRowChange(index, "contEndDate", val)}
            />

            <Input2Medium
              value={row?.atr ? `${row?.atr}%` : ""}
              onChange={(val) => {
                handleRowChange(index, "atr", val);
              }}
              options={ATROptions}
              per={true}
              type="per"
              // readOnly
            />
            <Input2Medium
              type="text"
              value={row?.type ?? ""}
              onChange={(val) => handleRowChange(index, "type", val)}
              options={InvestmentTypeOptions}
              readOnly
            />
            <RemoveButton onClick={() => handleRemove(index)} />
          </div>
          {errors[index] && row.type === "ISA" && (
            <>
              <div className="error Label">{errors[index]}</div>
              <div className="card-divider " />
            </>
          )}
        </div>
      ))}
    </div>
  );
};

export default InvestmentDialogBox;
